import { Bell, Smartphone, X } from 'lucide-react';
import { useEffect, useState } from 'react';

// 🔸 Helper local para pedir permissão (evita depender de notificationService)
function isNotificationAPISupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}
async function requestPermissionLocal(): Promise<NotificationPermission> {
  if (!isNotificationAPISupported()) return 'denied';
  const current = Notification.permission;
  if (current !== 'default') return current; // 'granted' | 'denied'
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

export default function SimpleNotificationPrompt() {
  // 🔕 Desligado por padrão até termos fluxo completo de Web Push
  const ENABLE = import.meta.env?.VITE_ENABLE_PUSH_PROMPT === 'true';

  const [show, setShow] = useState(false);
  const [requesting, setRequesting] = useState(false);

  // ✅ DETECTAR SE DEVE MOSTRAR PROMPT
  useEffect(() => {
    if (!ENABLE) return;
    const shouldShow = () => {
      // ✅ CONDIÇÕES PARA MOSTRAR:
      const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
      const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                    (window.navigator as any).standalone === true;
      const needsPermission = 'Notification' in window && Notification.permission === 'default';
      const notDismissedToday = localStorage.getItem('acor_notification_dismissed') !== new Date().toDateString();
      // Só mostra se houver capacidade de Web Push (SW + PushManager)
      const hasPush = 'serviceWorker' in navigator && 'PushManager' in window;
      return hasPush && (isMobile || isPWA) && needsPermission && notDismissedToday;
    };

    if (shouldShow()) {
      // Aguardar 3 segundos para não ser intrusivo
      const timer = setTimeout(() => setShow(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [ENABLE]);

  // ✅ SOLICITAR PERMISSÃO
  const requestPermissionHandler = async () => {
    if (!ENABLE) return;
    setRequesting(true);
    
    try {
      const perm = await requestPermissionLocal();
      if (perm === 'granted') {
        console.log('✅ ACOR: Notificações ativadas!');
        // Notificação de teste (via SW se disponível)
        try {
          const reg = await navigator.serviceWorker?.ready;
          if (reg?.showNotification) {
            reg.showNotification('🎉 ACOR ERP', {
              body: 'Notificações ativadas com sucesso!',
              icon: '/pwa-192x192.png',
            });
          } else {
            new Notification('🎉 ACOR ERP', {
              body: 'Notificações ativadas com sucesso!',
              icon: '/pwa-192x192.png'
            });
          }
        } catch {}
      }
      
      setShow(false);
    } catch (error) {
      console.error('Erro ao solicitar permissão:', error);
    } finally {
      setRequesting(false);
    }
  };

  // ✅ DISPENSAR
  const dismiss = () => {
    setShow(false);
    localStorage.setItem('acor_notification_dismissed', new Date().toDateString());
  };

  if (!ENABLE || !show) return null;

  return (
    <div className="fixed bottom-4 right-4 left-4 md:left-auto md:w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-50 p-4">
      
      {/* ✅ HEADER */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
            <Bell className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="font-ageo-bold text-gray-900 dark:text-gray-100 text-sm">
              Ativar Notificações
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Receba alertas de menções e tarefas
            </p>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* ✅ BOTÕES */}
      <div className="flex gap-2">
        <button
          onClick={dismiss}
          className="flex-1 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          Agora Não
        </button>
        <button
          onClick={requestPermissionHandler}
          disabled={requesting}
          className="flex-1 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {requesting ? (
            <>
              <div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" />
              Ativando...
            </>
          ) : (
            <>
              <Smartphone className="w-3 h-3" />
              Ativar
            </>
          )}
        </button>
      </div>
    </div>
  );
}
